import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { V1BEClient } from "@/clients";
import { IProductDetails } from "@/types/ProductTypes";
import SupplierCard from "./SupplierCard";

interface EventSuppliersProps {
  organizationEventId: string;
}

const EventSuppliers = ({ organizationEventId }: EventSuppliersProps) => {
  const [products, setProducts] = useState<IProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!organizationEventId) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const data = await V1BEClient.fetchProducts(
          `organization_event_id=${organizationEventId}&page=1&page_size=1000`
        );
        setProducts(data.data || []);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [organizationEventId]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return products;

    return products.filter((product) => {
      const title = (product.title || "").toLowerCase();
      const firstName = (product.user?.first_name || "").toLowerCase();
      const lastName = (product.user?.last_name || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const typeNames =
        product.product_types
          ?.map(
            (type) =>
              (type.product_type?.name || type.product_type_label || "").toLowerCase()
          )
          .join(" ") || "";

      return (
        title.includes(term) ||
        firstName.includes(term) ||
        lastName.includes(term) ||
        fullName.includes(term) ||
        typeNames.includes(term)
      );
    });
  }, [products, searchTerm]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Suppliers</h2>

      <div className="relative mb-6">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={20}
        />
        <input
          type="text"
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading suppliers...</p>
        </div>
      ) : filteredProducts.length > 0 ? (
        <>
          <p className="text-gray-600 mb-6">
            Showing {filteredProducts.length} supplier
            {filteredProducts.length !== 1 ? "s" : ""}
            {searchTerm && ` for "${searchTerm}"`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredProducts.map((product) => (
              <SupplierCard key={product.id} product={product} />
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search size={64} className="mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm ? "No suppliers found" : "No suppliers checked in yet"}
          </h3>
          <p className="text-gray-600">
            {searchTerm
              ? "Try adjusting your search keywords."
              : "Checked-in suppliers will appear here once they register for this event."}
          </p>
        </div>
      )}
    </div>
  );
};

export default EventSuppliers;
